// Health (spec/25 §16, F-BOOT-20): how the boot server and each PC are doing. Per PC: the 99th-percentile read latency, the
// cache-hit rate and the last boot time; for the server: its uplink use and disk queue over time. A figure turns amber
// past its threshold — latency over 5 ms, cache hits under 80 %, uplink over 85 % — and says so in words as well, never
// by colour alone. Every chart has its numbers in a table behind it. The charts are the kit's, drawn by the dataviz rules.

import type { BootMetricPoint, BootPc } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, BarChart, ChartEmpty, ChartTable, LineChart, SegmentedControl, StatCard, type BarDatum, type TableColumn } from '@clavian/ui-web';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { LoadFailure } from '../shell/LoadFailure';
import { Section, TableSkeleton } from '../ui';
import {
  formatMillis,
  formatPercent,
  HIT_AMBER_PCT,
  isHotUplink,
  isLowHit,
  isSlowP99,
  P99_AMBER_US,
  serverPoints,
  summarisePcs,
  UPLINK_AMBER_PCT,
  type PcHealth,
} from './format';
import { useBootMetrics, useBootPcs, type BootScope } from './queries';
import { useBootStations } from './stations';

const RANGES = ['hour', 'day', 'week', 'month'] as const;
type Range = (typeof RANGES)[number];
const RANGE_MS: Readonly<Record<Range, number>> = { hour: 3_600_000, day: 24 * 3_600_000, week: 7 * 24 * 3_600_000, month: 30 * 24 * 3_600_000 };

/** The instants a range covers, ending at the current minute so one minute is one cache key. */
export function rangeBounds(range: Range, now: number): { readonly from: string; readonly to: string } {
  const end = Math.floor(now / 60_000) * 60_000;
  return { from: new Date(end - RANGE_MS[range]).toISOString(), to: new Date(end).toISOString() };
}

export function HealthTab({ scope }: { readonly scope: BootScope }): ReactNode {
  const { t } = useTranslation('console');
  const [range, setRange] = useState<Range>('day');
  const [now, setNow] = useState(() => Date.now());
  // A new minute is a new read; the metrics are rolled up per minute.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const bounds = useMemo(() => rangeBounds(range, now), [range, now]);
  const metrics = useBootMetrics(scope, bounds);
  const pcs = useBootPcs(scope);
  const stations = useBootStations(scope.organizationId, scope.venueId);

  if (metrics.data === undefined) {
    return metrics.isError ? <LoadFailure error={metrics.error} message={t('boot.health.unavailable')} onRetry={() => void metrics.refetch()} /> : <TableSkeleton rows={5} label={t('boot.loading')} />;
  }
  return (
    <div className="console-boot__stack">
      <SegmentedControl
        label={t('boot.health.range.label')}
        value={range}
        onValueChange={setRange}
        options={RANGES.map((one) => ({ value: one, label: t(`boot.health.range.${one}`) }))}
      />
      {metrics.data.points.length === 0 ? (
        <ChartEmpty>{t('boot.health.empty')}</ChartEmpty>
      ) : (
        <HealthBody points={metrics.data.points} granularity={metrics.data.granularity} pcs={pcs.data ?? []} labelOf={(pc) => (pc.stationId === null ? pc.mac : (stations.get(pc.stationId)?.label ?? pc.mac))} />
      )}
    </div>
  );
}

function HealthBody({ points, granularity, pcs, labelOf }: { readonly points: readonly BootMetricPoint[]; readonly granularity: 'minute' | 'day'; readonly pcs: readonly BootPc[]; readonly labelOf: (pc: BootPc) => string }) {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const server = serverPoints(points);
  const perPc = summarisePcs(points);
  const pcById = new Map(pcs.map((pc) => [pc.id, pc]));
  const nameOf = (health: PcHealth): string => {
    const pc = pcById.get(health.bootPcId);
    return pc === undefined ? health.bootPcId : labelOf(pc);
  };

  const worstP99 = perPc.reduce<PcHealth | undefined>((worst, one) => (worst === undefined || one.p99Us > worst.p99Us ? one : worst), undefined);
  const lowestHit = perPc.reduce<PcHealth | undefined>((lowest, one) => (lowest === undefined || one.hitPct < lowest.hitPct ? one : lowest), undefined);
  const latest = server[server.length - 1];
  const uplink = latest?.uplinkPct ?? null;

  const latencyRows: BarDatum[] = [...perPc]
    .sort((a, b) => b.p99Us - a.p99Us)
    .map((one) => ({
      key: one.bootPcId,
      label: nameOf(one),
      value: one.p99Us,
      display: formatMillis(one.p99Us),
      ...(isSlowP99(one.p99Us) ? { meta: <Badge tone="warn" dot>{t('boot.health.slow')}</Badge> } : {}),
    }));
  const hitRows: BarDatum[] = [...perPc]
    .sort((a, b) => a.hitPct - b.hitPct)
    .map((one) => ({
      key: one.bootPcId,
      label: nameOf(one),
      value: one.hitPct,
      display: formatPercent(one.hitPct),
      ...(isLowHit(one.hitPct) ? { meta: <Badge tone="warn" dot>{t('boot.health.low')}</Badge> } : {}),
    }));
  const bootRows: BarDatum[] = pcs
    .filter((pc) => pc.lastBoot?.bootSeconds != null)
    .sort((a, b) => (b.lastBoot?.bootSeconds ?? 0) - (a.lastBoot?.bootSeconds ?? 0))
    .map((pc) => ({ key: pc.id, label: labelOf(pc), value: pc.lastBoot?.bootSeconds ?? 0, display: format.duration(pc.lastBoot?.bootSeconds ?? 0) }));

  const tableColumns: readonly TableColumn<PcHealth>[] = [
    { key: 'pc', header: t('boot.health.columns.pc'), cell: nameOf },
    { key: 'p99', header: t('boot.health.columns.p99'), numeric: true, cell: (one) => formatMillis(one.p99Us) },
    { key: 'hit', header: t('boot.health.columns.hit'), numeric: true, cell: (one) => formatPercent(one.hitPct) },
  ];
  const categoryLabel = (index: number): string => {
    const at = server[index]?.at;
    return at === undefined ? '' : granularity === 'day' ? format.date(at, 'medium') : format.dateTime(at, { date: 'medium', time: 'short' });
  };

  return (
    <>
      <div className="console-boot__stats">
        <StatCard
          label={t('boot.health.stats.uplink')}
          value={uplink === null ? '—' : formatPercent(uplink)}
          tone={uplink !== null && isHotUplink(uplink) ? 'warn' : 'default'}
          hint={uplink !== null && isHotUplink(uplink) ? t('boot.health.uplinkHot', { limit: UPLINK_AMBER_PCT }) : t('boot.health.uplinkOk')}
        />
        <StatCard
          label={t('boot.health.stats.queue')}
          value={latest?.diskQueue == null ? '—' : String(Math.round(latest.diskQueue * 10) / 10)}
          hint={t('boot.health.queueHint')}
        />
        <StatCard
          label={t('boot.health.stats.slowest')}
          value={worstP99 === undefined ? '—' : formatMillis(worstP99.p99Us)}
          tone={worstP99 !== undefined && isSlowP99(worstP99.p99Us) ? 'warn' : 'default'}
          hint={worstP99 === undefined ? undefined : isSlowP99(worstP99.p99Us) ? t('boot.health.slowHint', { pc: nameOf(worstP99), limit: P99_AMBER_US / 1000 }) : nameOf(worstP99)}
        />
        <StatCard
          label={t('boot.health.stats.lowestHit')}
          value={lowestHit === undefined ? '—' : formatPercent(lowestHit.hitPct)}
          tone={lowestHit !== undefined && isLowHit(lowestHit.hitPct) ? 'warn' : 'default'}
          hint={lowestHit === undefined ? undefined : isLowHit(lowestHit.hitPct) ? t('boot.health.lowHint', { pc: nameOf(lowestHit), limit: HIT_AMBER_PCT }) : nameOf(lowestHit)}
        />
      </div>

      <Section title={t('boot.health.uplinkChart.title')} description={t('boot.health.uplinkChart.intro', { limit: UPLINK_AMBER_PCT })}>
        {server.length === 0 ? (
          <ChartEmpty>{t('boot.health.empty')}</ChartEmpty>
        ) : (
          <LineChart
            label={t('boot.health.uplinkChart.title')}
            summary={t('boot.health.uplinkChart.summary', { peak: formatPercent(Math.max(...server.map((one) => one.uplinkPct ?? 0))) })}
            categories={server.map((one) => (granularity === 'day' ? format.custom(one.at, { day: 'numeric', month: 'short' }) : format.time(one.at, 'short')))}
            categoryLabel={categoryLabel}
            series={[{ key: 'uplink', label: t('boot.health.stats.uplink'), tone: 1, area: true, values: server.map((one) => one.uplinkPct) }]}
            format={formatPercent}
          />
        )}
      </Section>

      <div className="console-boot__charts">
        <Section title={t('boot.health.latencyChart.title')} description={t('boot.health.latencyChart.intro', { limit: P99_AMBER_US / 1000 })}>
          <BarChart label={t('boot.health.latencyChart.title')} rows={latencyRows} max={Math.max(P99_AMBER_US * 2, ...perPc.map((one) => one.p99Us))} />
        </Section>
        <Section title={t('boot.health.hitChart.title')} description={t('boot.health.hitChart.intro', { limit: HIT_AMBER_PCT })}>
          <BarChart label={t('boot.health.hitChart.title')} rows={hitRows} max={100} />
        </Section>
        {bootRows.length === 0 ? null : (
          <Section title={t('boot.health.bootChart.title')} description={t('boot.health.bootChart.intro')}>
            <BarChart label={t('boot.health.bootChart.title')} rows={bootRows} />
          </Section>
        )}
      </div>
      <ChartTable caption={t('boot.health.table')} columns={tableColumns} rows={perPc} rowKey={(one) => one.bootPcId} />
    </>
  );
}
